/**
 * orgFeatures
 * -----------
 * Per-organisation, display-only feature flags for the RMS provider surfaces.
 *
 * These flags never change what the server computes or stores — they only
 * decide what the clinician-facing UI surfaces. In particular
 * `riskStratificationVisible` gates the *display* of the automated
 * risk-stratification tier: the tier is still calculated and persisted
 * server-side exactly as before, so no data, sorting input or letter content
 * is lost. Hiding it is purely a clinician-facing choice.
 *
 * Blackpool / LCS (RMS-ORG-00001) asked for the automated tier to be hidden at
 * triage: clinical feedback was that the automated tier felt too subjective at
 * the triage stage. Every other organisation (and the demo) keeps the default
 * visible behaviour.
 */

// Safe default applied to every organisation unless explicitly overridden.
// New orgs and unknown/undefined ids fall through to this, preserving the
// existing "everything visible" behaviour.
const DEFAULT_ORG_FEATURES = {
  riskStratificationVisible: true,
  // Harbour SENCo portal (SEN & ND Access Portal, TDI-53): the redesigned
  // school-user surface. Off everywhere until an organisation is explicitly
  // onboarded, so existing school users keep the current portal untouched.
  harbourSencoPortal: false,
  // Harbour family portal (SEN & ND Access Portal, TDI-53): the redesigned
  // parent-or-carer surface. Off everywhere until an organisation is
  // explicitly onboarded, so existing guardians keep the current portal.
  harbourFamilyPortal: false,
};

// Per-org overrides, merged over the defaults. Only list what differs.
const ORG_FEATURE_OVERRIDES = {
  // Blackpool / LCS UAT org — hide the automated risk-stratification surfacing
  // (still computed and stored server-side; display only). The Harbour SEN &
  // ND Access Portal (TDI-53) is on for this org: school users and guardians
  // signing in land on the redesigned portals instead of the legacy
  // dashboard. This is the internal/UAT test org; no other org is onboarded.
  'RMS-ORG-00001': {
    riskStratificationVisible: false,
    harbourSencoPortal: true,
    harbourFamilyPortal: true,
  },
};

/**
 * Resolve the full feature map for an organisation.
 * Always returns a complete object: the defaults spread first, then any
 * per-org override. Unknown or undefined ids return the defaults untouched.
 *
 * @param {string} [orgId]
 * @returns {{ riskStratificationVisible: boolean }}
 */
export function getOrgFeatures(orgId) {
  return { ...DEFAULT_ORG_FEATURES, ...(ORG_FEATURE_OVERRIDES[orgId] || {}) };
}

/**
 * Whether the automated risk-stratification surfacing should be shown for this
 * organisation. Defaults to visible for unknown/undefined ids.
 *
 * @param {string} [orgId]
 * @returns {boolean}
 */
export function isRiskStratificationVisible(orgId) {
  return getOrgFeatures(orgId).riskStratificationVisible;
}

/**
 * Whether this organisation's school users get the Harbour SENCo portal
 * (SEN & ND Access Portal) instead of the legacy service-user dashboard.
 * Defaults to off for unknown/undefined ids.
 *
 * @param {string} [orgId]
 * @returns {boolean}
 */
export function isHarbourSencoPortalEnabled(orgId) {
  return getOrgFeatures(orgId).harbourSencoPortal === true;
}

/**
 * Whether the public referral form is closed in this organisation. Where the
 * Harbour SENCo portal is on, a SENCO gets an account only from the service
 * (registered, invited, sets a password), so the form offers schools no
 * other way in. Mirrors SERVICE_REGISTERED_SCHOOL_ORGS in
 * python-functions/rms_functions/rms_school_users.py.
 *
 * @param {string} [orgId]
 * @returns {boolean}
 */
export function isPublicReferralFormClosed(orgId) {
  return isHarbourSencoPortalEnabled(orgId);
}

/**
 * Whether this organisation's guardians get the Harbour family portal
 * (SEN & ND Access Portal) instead of the legacy service-user dashboard.
 * Defaults to off for unknown/undefined ids.
 *
 * @param {string} [orgId]
 * @returns {boolean}
 */
export function isHarbourFamilyPortalEnabled(orgId) {
  return getOrgFeatures(orgId).harbourFamilyPortal === true;
}
